import {
  Component,
  Input,
  HostListener,
  ChangeDetectorRef,
  OnDestroy,
} from "@angular/core";
import { Subscription } from "rxjs";
import { BroadcastPresetViewModel } from "../../view-models/broadcast-preset.view-model";
import { BroadcastPresetAudioChannel } from "app/js/src/domain/broadcasts/broadcast-preset-audio-channel";
import { ESwitcherAssetType } from "app/js/src/domain/broadcasts/switcher-item.type.enum";
import { ILayoutsRepository } from "app/js/src/domain/layouts/layouts.repository";

@Component({
  selector: "evrideo-broadcast-editor-audio-mixer",
  templateUrl: "./broadcast-editor-audio-mixer.component.html",
  styleUrl: "./broadcast-editor-audio-mixer.component.scss",
})
export class BroadcastEditorAudioMixerComponent implements OnDestroy {
  @Input() broadcastPresetViewModel: BroadcastPresetViewModel;
  private _channelMediaListChangedSubscription: Subscription | null = null;
  // Context menu state
  showContextMenu: boolean = false;
  contextMenuPosition: { x: number; y: number } = { x: 0, y: 0 };
  contextMenuTargetIndex: number | null = null;
  // Track which channel is being edited to prevent Angular from overwriting content
  editingChannelIndex: number | null = null;
  // Track hover position for tooltips (keyed by channel sourceUid)
  sliderHoverPositions: Map<string, number> = new Map();

  get audioChannels(): BroadcastPresetAudioChannel[] {
    return this.broadcastPresetViewModel.audioChannels;
  }
  get activeAudioChannels(): BroadcastPresetAudioChannel[] {
    var visibleChannels = this.audioChannels.filter((c) => c.isVisible);
    return [
      ...visibleChannels.filter(
        (c) => c.assetType === ESwitcherAssetType.Empty,
      ),
      ...visibleChannels.filter(
        (c) =>
          c.assetType !== ESwitcherAssetType.Empty &&
          c.assetType !== ESwitcherAssetType.LiveFeed,
      ),
      ...visibleChannels.filter(
        (c) => c.assetType === ESwitcherAssetType.LiveFeed,
      ),
    ];
  }

  dbScale = [-30, -24, -18, -12, -6, 0];
  volumeBarsCount = 80; // Number of volume bars to display
  volumeBars: number[] = Array(this.volumeBarsCount)
    .fill(0)
    .map((_, i) => i);

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _layoutRepository: ILayoutsRepository,
  ) {}

  ngOnDestroy(): void {
    if (this._channelMediaListChangedSubscription) {
      this._channelMediaListChangedSubscription.unsubscribe();
      this._channelMediaListChangedSubscription = null;
    }
    this.sliderHoverPositions.clear();
    this.broadcastPresetViewModel = null;
  }

  ngOnInit() {
    this._channelMediaListChangedSubscription =
      this.broadcastPresetViewModel.channelMediaListChanged.subscribe(
        async (isProgram) => {
        var assetsSourceUids = [];
        if (isProgram) {
          assetsSourceUids = this.broadcastPresetViewModel
            .getAssetsInProgram()
            .map((asset) => {
              return asset.type == ESwitcherAssetType.Block ||
                asset.type == ESwitcherAssetType.Stack
                ? asset.uid
                : asset.assetId.toString();
            });
        } else {
          assetsSourceUids = this.broadcastPresetViewModel
            .getAssetsInPreview()
            .map((asset) => {
              return asset.type == ESwitcherAssetType.Block ||
                asset.type == ESwitcherAssetType.Stack
                ? asset.uid
                : asset.assetId.toString();
            });
        }
        var layoutZoneMediaIds = await this._extractLayoutZoneMediaIds(
          isProgram
            ? this.broadcastPresetViewModel.getAssetsInProgram()
            : this.broadcastPresetViewModel.getAssetsInPreview(),
        );
        assetsSourceUids.push(...layoutZoneMediaIds);
        this._updateAudioChannelsVisibility(assetsSourceUids);
        },
      );
    var allAssets = this.broadcastPresetViewModel.getAllAssetItems();
    var activeAssets = allAssets.filter(
      (asset) => asset.isPreview || asset.isProgram,
    );
    var activeAssetsSourceUids = activeAssets.map((asset) => {
      return asset.type == ESwitcherAssetType.Block ||
        asset.type == ESwitcherAssetType.Stack
        ? asset.uid
        : asset.assetId.toString();
    });
    this._extractLayoutZoneMediaIds(activeAssets).then(
      async (layoutZoneMediaIds) => {
        activeAssetsSourceUids.push(...layoutZoneMediaIds);
        await this._updateAudioChannelsVisibility(activeAssetsSourceUids);
      },
    );
  }

  private async _extractLayoutZoneMediaIds(assets: any[]): Promise<string[]> {
    var layoutsUid: Set<string> = new Set();
    if (assets.some((asset) => asset.type === ESwitcherAssetType.Layout)) {
      await Promise.all(
        assets.map(async (asset) => {
          if (asset.type === ESwitcherAssetType.Layout) {
            var brokenDownSwitcherItems =
              await this.broadcastPresetViewModel.BreakdownLayoutSwitcherItem(
                asset,
              );
            brokenDownSwitcherItems.forEach((item) => {
              layoutsUid.add(item.assetId.toString());
            });
          }
        }),
      );
    }
    return Array.from(layoutsUid);
  }
  private _updateAudioChannelsVisibility(
    assetsSourceUids: string[],
    isVisible: boolean = true,
  ): void {
    // Then, set visibility for channels matching the active assets
    const visibleChannels: BroadcastPresetAudioChannel[] = [];
    for (const sourceUid of assetsSourceUids) {
      const channel = this.audioChannels.find((c) => c.sourceUid === sourceUid);
      if (channel) {
        channel.isVisible = isVisible;
        visibleChannels.push(channel);
      }
    }
    let allAssetItems = this.broadcastPresetViewModel
      .getAllAssetItems()
      .filter((asset) => ESwitcherAssetType.Empty != asset.type);
    allAssetItems
      .filter((asset) => asset.type === ESwitcherAssetType.Layout)
      .forEach(async (asset) => {
        var brokenDownSwitcherItems =
          await this.broadcastPresetViewModel.BreakdownLayoutSwitcherItem(
            asset,
          );
        brokenDownSwitcherItems.forEach((item) => {
          const aid = item.assetId?.toString() || "";
          const channel = this.audioChannels.find((c) => c.sourceUid === aid);
          if (channel) {
            if (
              item.type !== ESwitcherAssetType.LiveFeed &&
              visibleChannels.some((c) => c.sourceUid === channel.sourceUid) ===
                false &&
              !asset.isPreview &&
              !asset.isProgram
            ) {
              channel.isVisible = !isVisible;
            } else if (item.type === ESwitcherAssetType.LiveFeed) {
              this.audioChannels
                .filter((c) => c.sourceUid === aid)
                .forEach((c) => {
                  c.isVisible = true;
                });
            }
          }
        });
      });
    allAssetItems
      .filter((asset) => asset.type !== ESwitcherAssetType.Layout)
      .forEach(async (asset) => {
        if (asset.type == ESwitcherAssetType.LiveFeed) {
          return;
        }
        const aid =
          asset.type == ESwitcherAssetType.Block ||
          asset.type == ESwitcherAssetType.Stack
            ? asset.uid
            : asset.assetId?.toString() || "";
        const channel = this.audioChannels.find((c) => c.sourceUid === aid);
        if (
          channel &&
          visibleChannels.some((c) => c.sourceUid === channel.sourceUid) ===
            false
        ) {
          channel.isVisible = !isVisible;
        }
      });
    this._changeDetectorRef.detectChanges();
  }

  getVolumeBarColor(index: number): string {
    // Green bars for low levels (first ~40 bars)
    if (index < 40) {
      return "#55928e";
    }
    // Orange bars for mid levels (next ~20 bars)
    if (index < 60) {
      return "#f4a93c";
    }
    // Red bars for high levels (last ~20 bars)
    return "#ef5d5d";
  }

  onVolumeChange(
    channel: BroadcastPresetAudioChannel,
    value: string | number,
  ): void {
    var position = typeof value === "string" ? parseFloat(value) : value;
    // Ensure position is within valid range and is an integer
    position = Math.round(position);
    position = Math.max(0, Math.min(position, this.dbScale.length - 1));
    var db = this.dbScale[position];
    channel.volumeLevel = Math.pow(10, db / 20);
    this.broadcastPresetViewModel.updateAudioChannelVolume(channel);
  }
  volumeToSliderPosition(volume: number): number {
    var db = 20 * Math.log10(volume);
    // Find the closest index in dbScale instead of exact match
    var closestIndex = 0;
    var minDiff = Math.abs(db - this.dbScale[0]);
    for (var i = 1; i < this.dbScale.length; i++) {
      var diff = Math.abs(db - this.dbScale[i]);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = i;
      }
    }
    return closestIndex;
  }
  toNumber(value: string | number): number {
    return Number(value);
  }

  PositionToPercentage(position: number): number {
    return (position / (this.dbScale.length - 1)) * 100;
  }

  getDbValueForPosition(position: number): number {
    var index = Math.round(Number(position));
    index = Math.max(0, Math.min(index, this.dbScale.length - 1));
    return this.dbScale[index];
  }

  onSliderMouseMove(
    event: MouseEvent,
    channel: BroadcastPresetAudioChannel,
  ): void {
    const sliderElement = event.currentTarget as HTMLElement;
    const rect = sliderElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    // Convert percentage to position (0-5)
    const position = Math.round((percentage / 100) * (this.dbScale.length - 1));
    this.sliderHoverPositions.set(channel.sourceUid, position);
  }

  onSliderMouseLeave(channel: BroadcastPresetAudioChannel): void {
    this.sliderHoverPositions.delete(channel.sourceUid);
  }

  getTooltipForChannel(
    channel: BroadcastPresetAudioChannel,
    currentValue: number,
  ): string {
    const hoverPosition = this.sliderHoverPositions.get(channel.sourceUid);
    const position = hoverPosition !== undefined ? hoverPosition : currentValue;
    return this.getDbValueForPosition(position) + " dB";
  }

  onThreeDotsClick(
    index: number,
    event: MouseEvent,
    channel: BroadcastPresetAudioChannel,
  ): void {
    if (channel.isMaster) {
      return;
    }

    event.stopPropagation();
    this.contextMenuTargetIndex = index;

    // Position the context menu near the click
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    this.contextMenuPosition = {
      x: rect.right - 120,
      y: rect.top + 20,
    };

    this.showContextMenu = true;
  }

  toggleClearedForAir(): void {
    if (
      this.contextMenuTargetIndex !== null &&
      this.activeAudioChannels[this.contextMenuTargetIndex]
    ) {
      const channel = this.activeAudioChannels[this.contextMenuTargetIndex];
      channel.isClearedForAir = !channel.isClearedForAir;
      channel.isMuted = true;
      this.broadcastPresetViewModel.audioChannelUpdated(channel);
    }
    this.hideContextMenu();
  }

  private hideContextMenu(): void {
    this.showContextMenu = false;
    this.contextMenuTargetIndex = null;
  }

  // Hide context menu when clicking outside
  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (this.showContextMenu) {
      this.hideContextMenu();
    }
  }

  getSelectedChannel(): BroadcastPresetAudioChannel | null {
    if (
      this.contextMenuTargetIndex !== null &&
      this.activeAudioChannels[this.contextMenuTargetIndex]
    ) {
      return this.activeAudioChannels[this.contextMenuTargetIndex];
    }
    return null;
  }

  onChannelNameBlur(
    event: FocusEvent,
    channel: BroadcastPresetAudioChannel,
    index: number,
  ): void {
    if (channel.isMaster) {
      return;
    }

    this.editingChannelIndex = null;
    const newName = (event.target as HTMLElement).textContent?.trim() || "";
    if (newName !== channel.name && newName.length > 0) {
      channel.name = newName;
      this.broadcastPresetViewModel.audioChannelUpdated(channel);
    } else {
      // Reset to original name if empty or unchanged
      (event.target as HTMLElement).textContent = channel.name;
    }
  }

  onChannelNameFocus(index: number, event: FocusEvent): void {
    this.editingChannelIndex = index;
    // Ensure the element has the current name when focused
    const element = event.target as HTMLElement;
    if (element.textContent !== this.activeAudioChannels[index].name) {
      element.textContent = this.activeAudioChannels[index].name;
    }
  }

  onChannelNamePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData("text/plain") || "";
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode(text.replace(/\n/g, " "));
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }
  async onSoundClick(
    event: MouseEvent,
    channel: BroadcastPresetAudioChannel,
  ): Promise<void> {
    channel.isMuted = !channel.isMuted;
    await this.broadcastPresetViewModel.audioChannelUpdated(channel);
    this.broadcastPresetViewModel.ManageAudioChannelOnChannel(
      channel,
      channel.isMuted,
      true,
    );
    this._changeDetectorRef.detectChanges();
  }

  onHeadphonesMutedChange(
    muted: boolean,
    channel: BroadcastPresetAudioChannel,
  ): void {
    channel.previewListenMuted = muted;
    this.broadcastPresetViewModel.ManageAudioChannelOnChannel(
      channel,
      muted,
      false,
    );
    this._changeDetectorRef.detectChanges();
  }
}
